<template>
  <AnalyticsLayout>
    <div class="analytics-page">
      <!-- 页面标题和控制区域 -->
      <div class="mb-8">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold text-gray-800">{{ pageTitle }}</h2>
          <div class="flex items-center space-x-3">
            <!-- 桥梁选择器 -->
            <el-select
              v-model="selectedBridgeId"
              placeholder="请选择桥梁"
              filterable
              @change="onBridgeChange"
              class="w-48"
            >
              <el-option
                v-for="bridge in bridgeOptions"
                :key="bridge.id"
                :label="bridge.bridgeName"
                :value="bridge.id"
              />
            </el-select>
            
            <!-- 时间范围选择 -->
            <el-date-picker
              v-model="timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              @change="onTimeRangeChange"
              class="w-80"
            />
            
            <!-- 刷新按钮 -->
            <el-button 
              type="primary" 
              icon="el-icon-refresh" 
              @click="refreshData"
              :loading="loading"
              size="small"
            >
              刷新
            </el-button>
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div class="mb-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- 时间序列图表 -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex justify-between items-center mb-4">
              <div class="flex flex-col space-y-2">
                <h3 class="text-lg font-semibold text-gray-800">{{ timeSeriesChartTitle }}</h3>
                <!-- 流量统计时间粒度切换器 -->
                <div v-if="chartType === 'traffic'" class="flex items-center space-x-2">
                  <span class="text-sm text-gray-600">时间粒度：</span>
                  <el-radio-group v-model="timeGranularity" @change="onTimeGranularityChange" size="small">
                    <el-radio-button label="HOUR">按小时</el-radio-button>
                    <el-radio-button label="DAY">按天</el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <span>统计时间：{{ updateTime }}</span>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="refreshChartData"
                  :loading="chartLoading"
                  size="mini"
                >
                  刷新
                </el-button>
              </div>
            </div>
            <div ref="timeSeriesChart" class="h-80" v-loading="chartLoading"></div>
          </div>

          <!-- 分布图表 -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-800">{{ distributionChartTitle }}</h3>
              <div class="flex items-center space-x-2 text-sm text-gray-600">
                <span>统计时间：{{ updateTime }}</span>
                <el-button
                  type="text"
                  icon="el-icon-refresh"
                  @click="refreshChartData"
                  :loading="chartLoading"
                  size="mini"
                >
                  刷新
                </el-button>
              </div>
            </div>
            <div ref="distributionChart" class="h-80" v-loading="chartLoading"></div>
          </div>
        </div>
      </div>

      <!-- 数据表格区域 -->
      <div class="mb-8">
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-gray-800">{{ tableTitle }}</h3>
            <div class="flex items-center space-x-2">
              <el-button 
                type="primary" 
                icon="el-icon-download" 
                size="small"
                @click="exportData"
                :disabled="!hasTableData"
              >
                导出数据
              </el-button>
            </div>
          </div>
          
          
          <!-- 数据表格 -->
          <el-table
            :data="detailsTableData"
            :loading="detailsLoading"
            stripe
            border
            height="400"
            class="w-full"
            @sort-change="handleSortChange"
          >
            <el-table-column prop="eventTime" label="事件时间" width="160">
              <template slot-scope="scope">
                {{ formatDateTime(scope.row.eventTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="bridgeName" label="桥梁名称" width="120" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.bridgeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="areaName" label="区域名称" width="100" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.areaName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="vesselName" label="船舶名称" width="120" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.vesselName || scope.row.vesselMmsi || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="vesselTypeName" label="船舶类型" width="100">
              <template slot-scope="scope">
                {{ scope.row.vesselTypeName || '-' }}
              </template>
            </el-table-column>
              <el-table-column prop="eventType" label="事件类型" width="100">
                <template slot-scope="scope">
                  {{ getEventTypeName(scope.row.eventType, scope.row.ruleType) }}
                </template>
              </el-table-column>
            <el-table-column prop="statusName" label="处理状态" width="100">
              <template slot-scope="scope">
                <el-tag 
                  :type="getStatusTagType(scope.row.status)"
                  size="mini"
                >
                  {{ scope.row.statusName || '-' }}
                </el-tag>
              </template>
            </el-table-column>
              <el-table-column prop="speed" label="航速" width="100">
                <template slot-scope="scope">
                  {{ scope.row.speed != null ? `${scope.row.speed.toFixed(1)}节` : '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="heading" label="艏向" width="100">
                <template slot-scope="scope">
                  {{ formatHeading(scope.row.heading) }}
                </template>
              </el-table-column>
              <el-table-column prop="longitude" label="经度" width="150">
                <template slot-scope="scope">
                  {{ formatLongitude(scope.row.longitude) }}
                </template>
              </el-table-column>
              <el-table-column prop="latitude" label="纬度" width="150">
                <template slot-scope="scope">
                  {{ formatLatitude(scope.row.latitude) }}
                </template>
              </el-table-column>
                <el-table-column prop="approachDirectionName" label="接近方向" width="100">
                  <template slot-scope="scope">
                    {{ scope.row.approachDirectionName || '-' }}
                  </template>
                </el-table-column>
                <el-table-column v-if="showAlertMessage" prop="alertMessage" label="告警消息" width="300" show-overflow-tooltip>
                  <template slot-scope="scope">
                    {{ scope.row.alertMessage || '-' }}
                  </template>
                </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <div class="flex justify-between items-center mt-4">
            <div class="text-sm text-gray-500">
              共 {{ detailsPagination.total }} 条记录
            </div>
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="detailsPagination.pageNum"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="detailsPagination.pageSize"
              :total="detailsPagination.total"
              layout="total, sizes, prev, pager, next, jumper"
            >
            </el-pagination>
          </div>
        </div>
      </div>
      </div>
  </AnalyticsLayout>
</template>

<script>
import AnalyticsLayout from '@/components/AnalyticsLayout.vue'
import { getCurrentAccountStatistics } from '@/api/account-statistics'
import { getAllBridges } from '@/api/bridge'
import { getYawDeviationStatistics, getHeightViolationStatistics, getSpeedViolationStatistics, getTrafficFlowStatistics, getRestrictedAreaStatistics, getVhfStatistics, getComprehensiveAlertStatistics, getStatisticsDetails, exportStatisticsDetailsToCSV } from '@/api/comprehensive-query'
import bridgeStateManager from '@/utils/bridgeStateManager'
import { convertToDMS } from '@/utils/coordinateUtils'
import * as echarts from 'echarts'

export default {
  name: 'AnalyticsTemplate',
  components: {
    AnalyticsLayout
  },
  props: {
    pageTitle: {
      type: String,
      default: '统计分析'
    },
    chartType: {
      type: String,
      default: 'deviation' // deviation, superelevation, overspeed, traffic, forbid, vhf, alert
    }
  },
  data() {
    return {
      selectedBridgeId: bridgeStateManager.getCurrentBridgeId(),
      timeRange: this.getDefaultTimeRange(),
      bridgeOptions: bridgeStateManager.getBridgeList(),
      timeGranularity: 'HOUR', // 时间粒度：HOUR（按小时）、DAY（按天）
      loading: false,
      chartLoading: false,
      tableLoading: false,
      detailsLoading: false,
      exportLoading: false,
      detailsTableData: [],
      detailsPagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      detailsQuery: {
        mmsiOrName: '',
        vesselType: '',
        alertLevel: '',
        severityLevel: null,
        communicationType: ''
      },
      updateTime: '',
      
      // 图表实例
      timeSeriesChart: null,
      distributionChart: null,
      
      // 统计数据
      statisticsData: {
        totalCount: 0,
        timeSeriesData: [],
        vesselTypeData: [],
        levelData: [],
        typeData: [],
        statusData: [],
        startTime: null,
        endTime: null
      },
      
      // 原始按小时数据缓存（用于按天聚合）
      hourlyDataCache: []
    }
  },
  
  computed: {
    timeSeriesChartTitle() {
      const granularityText = this.timeGranularity === 'HOUR' ? '按小时' : '按天'
      
      const typeMap = {
        'deviation': `偏航趋势分析-按天 (总偏航次数：${this.statisticsData.totalCount || 0})`,
        'superelevation': `超高趋势分析-按天 (总超高次数：${this.statisticsData.totalCount || 0})`, 
        'overspeed': `超速趋势分析-按天 (总超速次数：${this.statisticsData.totalCount || 0})`,
        'traffic': `流量趋势分析-${granularityText} (总通航次数：${this.statisticsData.totalCount || 0})`,
        'forbid': `禁航趋势分析-按天 (总禁航次数：${this.statisticsData.totalCount || 0})`,
        'vhf': `VHF趋势分析-按天 (总呼叫次数：${this.statisticsData.totalCount || 0})`,
        'alert': `预警趋势分析-按天 (总预警次数：${this.statisticsData.totalCount || 0})`
      }
      return typeMap[this.chartType] || '趋势分析'
    },
    
    distributionChartTitle() {
      const typeMap = {
        'deviation': '船舶类型分布',
        'superelevation': '船舶类型分布',
        'overspeed': '船舶类型分布', 
        'traffic': '船舶类型分布',
        'forbid': '船舶类型分布',
        'vhf': '船舶类型分布',
        'alert': '船舶类型分布'
      }
      return typeMap[this.chartType] || '分布分析'
    },
    
    tableTitle() {
      return `${this.pageTitle}明细数据`
    },
    
    hasTableData() {
      // 检查是否有详情数据
      return this.detailsTableData && this.detailsTableData.length > 0
    },

    // 是否显示告警消息列（仅VHF和预警统计显示）
    showAlertMessage() {
      return this.chartType === 'vhf' || this.chartType === 'alert'
    },

    // 获取当前分布数据
    currentDistributionData() {
      // 所有统计类型都使用vesselTypeData（船舶类型统计）
      return this.statisticsData.vesselTypeData || []
    },

    // 获取统计类型映射
  // YAW：偏航事件
  // CROSSING：穿越事件，用于流量统计
  // HEIGHT_VIOLATION, SPEED_VIOLATION：可能是预留的事件类型

    // statisticsTypeMapping() {
    //   const mapping = {
    //     'deviation': 'YAW',
    //     'superelevation': 'HEIGHT_VIOLATION', 
    //     'overspeed': 'SPEED_VIOLATION',
    //     'traffic': 'TRAFFIC_FLOW',
    //     'forbid': 'RESTRICTED_AREA',
    //     'vhf': 'VHF',
    //     'alert': 'COMPREHENSIVE_ALERT'
    //   }
    //   return mapping[this.chartType] || 'YAW'
    // }

    statisticsTypeMapping() {
      const mapping = {
        'deviation': 'YAW',          // ✅ 桥梁偏航统计 -> YAW
        'superelevation': 'HIGH',    // ✅ 桥梁超高统计 -> HIGH  
        'overspeed': 'SPEED',        // ✅ 桥梁超速统计 -> SPEED
        'traffic': 'CROSSING',       // ✅ 流量统计 -> CROSSING
        'forbid': 'FORBID',         // ✅ 桥梁禁航统计 -> FORBID（暂时保留映射，虽然菜单项不需要）
        'vhf': 'VHF',               // ✅ 桥梁VHF统计 -> VHF
        'alert': 'ALERT'            // ✅ 桥梁预警统计 -> ALERT
      }
      return mapping[this.chartType] || 'YAW';
    }
  },
  
  mounted() {
    this.loadBridgeOptions()
    this.updateTime = this.formatTime()
    this.initCharts()
    this.loadDetailsData()
    
    // 监听桥梁状态变化
    this.bridgeChangeUnsubscribe = bridgeStateManager.onBridgeChange((newBridgeId) => {
      if (this.selectedBridgeId !== newBridgeId) {
        this.selectedBridgeId = newBridgeId
        this.onBridgeChange()
      }
    })
  },
  
  beforeDestroy() {
    this.destroyCharts()
    // 取消桥梁状态变化监听
    if (this.bridgeChangeUnsubscribe) {
      this.bridgeChangeUnsubscribe()
    }
  },
  
  methods: {
    // 获取默认时间范围（最近7天）
    getDefaultTimeRange() {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      
      return [
        this.formatDateTime(start),
        this.formatDateTime(end)
      ]
    },
    
    // 格式化日期时间
    formatDateTime(date) {
      if (!date) {
        return '-'
      }
      
      let dateObj
      if (typeof date === 'string') {
        // 如果是字符串，直接返回（API已经格式化好了）
        return date
      } else if (date instanceof Date) {
        dateObj = date
      } else {
        // 尝试转换为Date对象
        dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) {
          return '-'
        }
      }
      
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      const hour = String(dateObj.getHours()).padStart(2, '0')
      const minute = String(dateObj.getMinutes()).padStart(2, '0')
      const second = String(dateObj.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    },
    
    // 格式化当前时间
    formatTime() {
      return this.formatDateTime(new Date())
    },
    
    // 加载桥梁选项（统一数据源）
    async loadBridgeOptions() {
      this.loading = true
      try {
        const response = await getAllBridges()
        if (response.code === 200 && response.data) {
          // 倒序排列桥梁列表
          this.bridgeOptions = (response.data || [])
            .reverse()
            .map(bridge => ({
              id: bridge.id,
              bridgeName: bridge.bridgeName || bridge.name || `桥梁${bridge.id}`,
              bridgeCode: bridge.bridgeCode,
              latitude: bridge.latitude,
              longitude: bridge.longitude
            }))
          
          // 保存桥梁列表到localStorage
          bridgeStateManager.setBridgeList(this.bridgeOptions)
          
          // 如果没有选中桥梁或选中的桥梁不在列表中，默认选择第一个
          if (!this.selectedBridgeId || !this.bridgeOptions.find(bridge => bridge.id === this.selectedBridgeId)) {
            if (this.bridgeOptions.length > 0) {
              this.selectedBridgeId = this.bridgeOptions[0].id
              bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
            }
          }
          
          if (this.selectedBridgeId) {
            this.$nextTick(() => {
              this.initCharts()
              this.refreshChartData()
            })
          }
          
          console.log('【AnalyticsTemplate】加载桥梁列表成功:', this.bridgeOptions.length, '条记录')
        } else {
          this.$message.error(response.message || '加载桥梁列表失败')
        }
      } catch (error) {
        console.error('【AnalyticsTemplate】加载桥梁列表失败:', error)
        this.$message.error('加载桥梁列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 桥梁变化处理
    onBridgeChange() {
      // 保存桥梁ID到localStorage
      bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
      
      if (this.selectedBridgeId) {
        this.$nextTick(() => {
          this.initCharts()
          this.refreshChartData()
          this.loadDetailsData()
        })
      }
    },
    
    // 时间范围变化处理
    onTimeRangeChange() {
      this.refreshChartData()
      this.loadDetailsData()
    },
    
    // 时间粒度变化处理
    onTimeGranularityChange() {
      if (this.chartType === 'traffic') {
        if (this.timeGranularity === 'DAY' && this.hourlyDataCache.length > 0) {
          // 如果有缓存的按小时数据，直接聚合
          this.aggregateHourlyToDaily()
          this.renderCharts()
        } else if (this.timeGranularity === 'HOUR') {
          // 恢复按小时数据
          this.statisticsData.timeSeriesData = [...this.hourlyDataCache]
          this.renderCharts()
        } else {
          // 重新获取数据
          this.refreshChartData()
        }
        this.updateTime = this.formatTime()
      }
    },
    
    // 刷新数据
    refreshData() {
      this.refreshChartData()
      this.loadDetailsData()
      this.updateTime = this.formatTime()
    },
    
    // 刷新图表数据
    async refreshChartData() {
      if (!this.selectedBridgeId) {
        this.$message.warning('请先选择桥梁')
        return
      }
      
      if (!this.timeRange || this.timeRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return
      }
      
      this.chartLoading = true
      
      try {
        const params = {
          bridgeId: this.selectedBridgeId,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          timeGranularity: 'HOUR' // 始终使用HOUR获取原始数据
        }
        
        let response
        switch (this.chartType) {
          case 'deviation':
            response = await getYawDeviationStatistics(params)
            break
          case 'superelevation':
            response = await getHeightViolationStatistics(params)
            break
          case 'overspeed':
            response = await getSpeedViolationStatistics(params)
            break
          case 'traffic':
            response = await getTrafficFlowStatistics(params)
            break
          case 'forbid':
            response = await getRestrictedAreaStatistics(params)
            break
          case 'vhf':
            response = await getVhfStatistics(params)
            break
          case 'alert':
            response = await getComprehensiveAlertStatistics(params)
            break
          default:
            response = await getYawDeviationStatistics(params)
        }
        
        if (response.code === 200) {
          // 保存原始按小时数据
          if (this.chartType === 'traffic' || this.chartType === 'alert' || this.chartType === 'vhf') {
            this.hourlyDataCache = response.data.timeSeriesData || []
          }
          
          this.statisticsData = response.data
          
          // 如果是流量统计且选择按天显示，进行数据聚合
          if (this.chartType === 'traffic' && this.timeGranularity === 'DAY') {
            this.aggregateHourlyToDaily()
          }
          
          // 如果是预警统计，始终按天聚合（按告警类型分组）
          if (this.chartType === 'alert') {
            this.aggregateAlertHourlyToDaily()
          }
          
          // 如果是VHF统计，始终按天聚合
          if (this.chartType === 'vhf') {
            this.aggregateVhfHourlyToDaily()
          }
          
          this.renderCharts()
          this.updateTime = this.formatTime()
        } else {
          this.$message.error(response.message || '加载统计数据失败')
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
        this.$message.error('加载统计数据失败')
      } finally {
        this.chartLoading = false
      }
    },
    
    // 初始化图表
    initCharts() {
      this.$nextTick(() => {
        if (this.$refs.timeSeriesChart && this.$refs.distributionChart) {
          this.renderCharts()
        }
      })
    },
    
    // 渲染图表
    renderCharts() {
      this.renderTimeSeriesChart()
      this.renderDistributionChart()
    },
    
    // 渲染时间序列图表
    renderTimeSeriesChart() {
      if (!this.$refs.timeSeriesChart) return
      
      if (this.timeSeriesChart) {
        this.timeSeriesChart.dispose()
      }
      
      this.timeSeriesChart = echarts.init(this.$refs.timeSeriesChart)
      
      // 确保时间序列数据按时间升序排列
      const sortedTimeSeriesData = [...(this.statisticsData.timeSeriesData || [])].sort((a, b) => {
        const timeA = new Date(a.time).getTime()
        const timeB = new Date(b.time).getTime()
        return timeA - timeB
      })
      
      // 预警统计使用分组柱状图
      if (this.chartType === 'alert') {
        this.renderAlertGroupedBarChart(sortedTimeSeriesData)
        return
      }
      
      // 是否显示数据标签
      // 流量统计：按天显示标签，按小时不显示；其他类型：始终显示
      const showLabel = this.chartType === 'traffic' 
        ? this.timeGranularity === 'DAY' 
        : this.chartType !== 'traffic'
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: showLabel ? '15%' : '8%', // 显示标签时增加顶部空间
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: sortedTimeSeriesData.map(item => item.timeLabel || item.time),
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: '次数'
        },
        series: [
          {
            name: '统计数量',
            type: 'bar',
            data: sortedTimeSeriesData.map(item => item.value || 0),
            itemStyle: {
              color: '#3b82f6'
            },
            label: {
              show: showLabel,
              position: 'top',
              fontSize: 12,
              color: '#333',
              fontWeight: 'bold'
            }
          }
        ]
      }
      
      this.timeSeriesChart.setOption(option)
    },
    
    // 渲染预警统计分组柱状图
    renderAlertGroupedBarChart(sortedTimeSeriesData) {
      // 收集所有告警类型
      const eventTypeSet = new Set()
      sortedTimeSeriesData.forEach(item => {
        const eventTypeCounts = item.extraData?.eventTypeCounts || {}
        Object.keys(eventTypeCounts).forEach(eventType => {
          eventTypeSet.add(eventType)
        })
      })
      
      // 告警类型列表（按固定顺序排列）
      const eventTypeOrder = ['ENTRY', 'EXIT', 'CROSSING', 'YAW', 'SPEED', 'HIGH', 'STAY', 'BLACKLIST']
      const eventTypes = Array.from(eventTypeSet).sort((a, b) => {
        const indexA = eventTypeOrder.indexOf(a)
        const indexB = eventTypeOrder.indexOf(b)
        if (indexA === -1 && indexB === -1) return a.localeCompare(b)
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
      })
      
      // 告警类型中文名称映射
      const eventTypeNameMap = {
        'ENTRY': '进入',
        'EXIT': '离开',
        'CROSSING': '穿越',
        'YAW': '偏航',
        'SPEED': '超速',
        'HIGH': '超高',
        'STAY': '滞留',
        'BLACKLIST': '黑名单'
      }
      
      // 告警类型颜色配置
      const eventTypeColors = {
        'ENTRY': '#10b981',      // 绿色
        'EXIT': '#3b82f6',       // 蓝色
        'CROSSING': '#f59e0b',   // 橙色
        'YAW': '#ef4444',        // 红色
        'SPEED': '#8b5cf6',      // 紫色
        'HIGH': '#ec4899',       // 粉色
        'STAY': '#06b6d4',       // 青色
        'BLACKLIST': '#6b7280'   // 灰色
      }
      
      // 为每个告警类型创建系列
      const series = eventTypes.map(eventType => {
        return {
          name: eventTypeNameMap[eventType] || eventType,
          type: 'bar',
          data: sortedTimeSeriesData.map(item => {
            const eventTypeCounts = item.extraData?.eventTypeCounts || {}
            return eventTypeCounts[eventType] || 0
          }),
          itemStyle: {
            color: eventTypeColors[eventType] || '#3b82f6'
          },
          label: {
            show: true,
            position: 'top',
            fontSize: 11,
            color: '#333',
            fontWeight: 'normal'
          }
        }
      })
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            let result = params[0].name + '<br/>'
            let total = 0
            params.forEach(param => {
              result += param.marker + param.seriesName + ': ' + param.value + '<br/>'
              total += param.value
            })
            result += '<span style="font-weight: bold;">总计: ' + total + '</span>'
            return result
          }
        },
        legend: {
          data: eventTypes.map(type => eventTypeNameMap[type] || type),
          top: 10,
          left: 'center'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '20%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: sortedTimeSeriesData.map(item => item.timeLabel || item.time),
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: '次数'
        },
        series: series
      }
      
      this.timeSeriesChart.setOption(option)
    },
    
    // 渲染分布图表
    renderDistributionChart() {
      if (!this.$refs.distributionChart) return
      
      if (this.distributionChart) {
        this.distributionChart.dispose()
      }
      
      this.distributionChart = echarts.init(this.$refs.distributionChart)
      
      const data = this.currentDistributionData
      
      // 饼图始终显示数据标签
      const showLabel = true
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 10,
          data: data.map(item => item.vesselTypeName || item.name || '未知')
        },
        series: [
          {
            name: this.distributionChartTitle,
            type: 'pie',
            radius: '50%',
            center: ['60%', '50%'],
            data: data.map(item => ({
              value: item.count || item.value || 0,
              name: item.vesselTypeName || item.name || '未知'
            })),
            label: {
              show: showLabel,
              position: 'outside',
              formatter: showLabel ? '{b}\n{c} ({d}%)' : '{b}',
              fontSize: 12,
              color: '#333',
              fontWeight: 'bold',
              lineHeight: 16
            },
            labelLine: {
              show: showLabel,
              length: 15,
              length2: 10,
              smooth: false
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      
      this.distributionChart.setOption(option)
    },
    
    // 销毁图表
    destroyCharts() {
      if (this.timeSeriesChart) {
        this.timeSeriesChart.dispose()
        this.timeSeriesChart = null
      }
      if (this.distributionChart) {
        this.distributionChart.dispose()
        this.distributionChart = null
      }
    },
    
    // 将按小时数据聚合为按天数据（用于流量统计）
    aggregateHourlyToDaily() {
      if (!this.hourlyDataCache || this.hourlyDataCache.length === 0) {
        return
      }
      
      // 按日期分组聚合
      const dailyMap = new Map()
      
      this.hourlyDataCache.forEach(item => {
        // 从时间字符串中提取日期部分
        let dateStr = null
        if (item.time) {
          dateStr = item.time.split(' ')[0]
        } else if (item.timeLabel) {
          dateStr = item.timeLabel.split(' ')[0]
        }
        
        if (!dateStr) return
        
        if (dailyMap.has(dateStr)) {
          dailyMap.set(dateStr, dailyMap.get(dateStr) + (item.value || 0))
        } else {
          dailyMap.set(dateStr, item.value || 0)
        }
      })
      
      // 转换为数组格式并排序
      const dailyData = Array.from(dailyMap.entries())
        .map(([date, value]) => ({
          time: date,
          timeLabel: date,
          value: value
        }))
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
      
      // 更新统计数据
      this.statisticsData.timeSeriesData = dailyData
      
      console.log('数据聚合完成:', {
        原始数据条数: this.hourlyDataCache.length,
        聚合后数据条数: dailyData.length,
        聚合后数据: dailyData
      })
    },
    
    // 将预警统计按小时数据聚合为按天数据（保留告警类型分组）
    aggregateAlertHourlyToDaily() {
      if (!this.hourlyDataCache || this.hourlyDataCache.length === 0) {
        return
      }
      
      // 按日期分组聚合，保留告警类型统计
      const dailyMap = new Map()
      
      this.hourlyDataCache.forEach(item => {
        // 从时间字符串中提取日期部分
        let dateStr = null
        if (item.time) {
          // 处理完整日期时间格式 "2025-11-04 09:00:00"
          const timeStr = item.time.split(' ')[0]
          if (timeStr && timeStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dateStr = timeStr
          }
        }
        
        if (!dateStr && item.timeLabel) {
          // 处理格式如 "11-04 09:00" 的情况
          const parts = item.timeLabel.split(' ')
          if (parts.length > 0) {
            const datePart = parts[0] // "11-04"
            // 尝试从time字段获取年份，如果没有则使用当前年份
            let year = new Date().getFullYear()
            if (item.time) {
              const timeMatch = item.time.match(/^(\d{4})-/)
              if (timeMatch) {
                year = parseInt(timeMatch[1])
              }
            }
            dateStr = `${year}-${datePart}`
          }
        }
        
        if (!dateStr) return
        
        // 获取该时间点的告警类型统计
        const eventTypeCounts = item.extraData?.eventTypeCounts || {}
        
        if (dailyMap.has(dateStr)) {
          const existing = dailyMap.get(dateStr)
          // 累加总数
          existing.value = (existing.value || 0) + (item.value || 0)
          // 累加各告警类型数量
          Object.keys(eventTypeCounts).forEach(eventType => {
            existing.eventTypeCounts[eventType] = (existing.eventTypeCounts[eventType] || 0) + (eventTypeCounts[eventType] || 0)
          })
        } else {
          dailyMap.set(dateStr, {
            value: item.value || 0,
            eventTypeCounts: { ...eventTypeCounts }
          })
        }
      })
      
      // 转换为数组格式并排序
      const dailyData = Array.from(dailyMap.entries())
        .map(([date, data]) => {
          // 格式化日期标签（显示为 MM-DD 格式）
          const dateObj = new Date(date)
          const month = String(dateObj.getMonth() + 1).padStart(2, '0')
          const day = String(dateObj.getDate()).padStart(2, '0')
          const timeLabel = `${month}-${day}`
          
          return {
            time: date,
            timeLabel: timeLabel,
            value: data.value,
            extraData: {
              eventTypeCounts: data.eventTypeCounts
            }
          }
        })
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
      
      // 更新统计数据
      this.statisticsData.timeSeriesData = dailyData
      
      console.log('预警统计数据聚合完成:', {
        原始数据条数: this.hourlyDataCache.length,
        聚合后数据条数: dailyData.length,
        聚合后数据: dailyData
      })
    },
    
    // 将VHF统计按小时数据聚合为按天数据
    aggregateVhfHourlyToDaily() {
      if (!this.hourlyDataCache || this.hourlyDataCache.length === 0) {
        return
      }
      
      // 按日期分组聚合，累加呼叫次数和唯一船舶数
      const dailyMap = new Map()
      
      this.hourlyDataCache.forEach(item => {
        // 从时间字符串中提取日期部分
        let dateStr = null
        if (item.time) {
          // 处理完整日期时间格式 "2025-11-04 10:00:00"
          const timeStr = item.time.split(' ')[0]
          if (timeStr && timeStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dateStr = timeStr
          }
        }
        
        if (!dateStr && item.timeLabel) {
          // 处理格式如 "11-04 10:00" 的情况
          const parts = item.timeLabel.split(' ')
          if (parts.length > 0) {
            const datePart = parts[0] // "11-04"
            // 尝试从time字段获取年份，如果没有则使用当前年份
            let year = new Date().getFullYear()
            if (item.time) {
              const timeMatch = item.time.match(/^(\d{4})-/)
              if (timeMatch) {
                year = parseInt(timeMatch[1])
              }
            }
            dateStr = `${year}-${datePart}`
          }
        }
        
        if (!dateStr) return
        
        // 获取该时间点的VHF统计数据
        const value = item.value || 0
        const uniqueVessels = item.extraData?.uniqueVessels || 0
        
        if (dailyMap.has(dateStr)) {
          const existing = dailyMap.get(dateStr)
          // 累加呼叫次数
          existing.value = (existing.value || 0) + value
          // 累加唯一船舶数（按天统计时，累加各小时的唯一船舶数）
          existing.uniqueVessels = (existing.uniqueVessels || 0) + uniqueVessels
        } else {
          dailyMap.set(dateStr, {
            value: value,
            uniqueVessels: uniqueVessels
          })
        }
      })
      
      // 转换为数组格式并排序
      const dailyData = Array.from(dailyMap.entries())
        .map(([date, data]) => {
          // 格式化日期标签（显示为 MM-DD 格式）
          const dateObj = new Date(date)
          const month = String(dateObj.getMonth() + 1).padStart(2, '0')
          const day = String(dateObj.getDate()).padStart(2, '0')
          const timeLabel = `${month}-${day}`
          
          return {
            time: date,
            timeLabel: timeLabel,
            value: data.value,
            extraData: {
              uniqueVessels: data.uniqueVessels
            }
          }
        })
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
      
      // 更新统计数据
      this.statisticsData.timeSeriesData = dailyData
      
      console.log('VHF统计数据聚合完成:', {
        原始数据条数: this.hourlyDataCache.length,
        聚合后数据条数: dailyData.length,
        聚合后数据: dailyData
      })
    },
    
    // 加载详情数据
    async loadDetailsData() {
      if (!this.selectedBridgeId) {
        return
      }
      
      this.detailsLoading = true
      try {
        const params = {
          statisticsType: this.statisticsTypeMapping,
          bridgeId: this.selectedBridgeId,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          pageNum: this.detailsPagination.pageNum,
          pageSize: this.detailsPagination.pageSize,
          ...this.detailsQuery
        }
        
        // 过滤空值
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })
        
        const response = await getStatisticsDetails(params)
        if (response.code === 200) {
          this.detailsTableData = response.data.list || []
          this.detailsPagination = {
            pageNum: response.data.pageNum,
            pageSize: response.data.pageSize,
            total: response.data.total
          }
        } else {
          this.$message.error(response.message || '加载详情数据失败')
        }
      } catch (error) {
        console.error('加载详情数据失败:', error)
        this.$message.error('加载详情数据失败')
      } finally {
        this.detailsLoading = false
      }
    },

    // 分页大小变化
    handleSizeChange(val) {
      this.detailsPagination.pageSize = val
      this.detailsPagination.pageNum = 1
      this.loadDetailsData()
    },

    // 当前页变化
    handleCurrentChange(val) {
      this.detailsPagination.pageNum = val
      this.loadDetailsData()
    },

    // 排序变化
    handleSortChange({ column, prop, order }) {
      // 这里可以根据需要实现排序逻辑
      console.log('排序变化:', { column, prop, order })
    },

    // 查看详情记录
    viewDetailRecord(row) {
      this.$message.info(`查看记录详情：${row.id}`)
      // 这里可以打开详情对话框
    },

    // 获取严重级别标签类型
    getSeverityTagType(level) {
      const typeMap = {
        1: 'info',
        2: 'success', 
        3: 'warning',
        4: 'danger',
        5: 'danger'
      }
      return typeMap[level] || 'info'
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        0: 'warning', // 未处理
        1: 'success', // 已处理
        2: 'info'     // 已忽略
      }
      return typeMap[status] || 'info'
    },

    // 获取事件类型中文名称
    getEventTypeName(eventType, ruleType) {
      // 对于VHF和预警统计，优先使用ruleType
      if ((this.chartType === 'vhf' || this.chartType === 'alert') && ruleType) {
        // 处理包含EXIT和ENTRY的规则类型
        if (ruleType.includes('EXIT')) {
          return '离开'
        }
        if (ruleType.includes('ENTRY')) {
          return '进入'
        }
        
        const ruleTypeMap = {
          'YAW': '偏航',
          'CROSSING': '穿越', 
          'SPEED': '超速',
          'HIGH': '超高',
          'STAY': '滞留',
          'BLACKLIST': '黑名单'
        }
        return ruleTypeMap[ruleType] || ruleType
      }
      
      // 其他统计类型或没有ruleType时，使用eventType
      if (!eventType) return '-'
      
      // 处理包含EXIT和ENTRY的事件类型
      if (eventType.includes('EXIT')) {
        return '离开'
      }
      if (eventType.includes('ENTRY')) {
        return '进入'
      }
      
      const typeMap = {
        'YAW': '偏航',
        'CROSSING': '穿越', 
        'SPEED': '超速',
        'HIGH': '超高',
        'STAY': '滞留',
        'BLACKLIST': '黑名单'
      }
      return typeMap[eventType] || eventType
    },

    // 格式化经度为度分秒格式
    formatLongitude(longitude) {
      return convertToDMS(longitude, 'lng')
    },

    // 格式化纬度为度分秒格式
    formatLatitude(latitude) {
      return convertToDMS(latitude, 'lat')
    },

    // 格式化艏向
    formatHeading(heading) {
      if (heading == null || heading > 360) {
        return '-'
      }
      return `${heading.toFixed(1)}°`
    },

    // 导出详情数据
    async exportDetailsData() {
      if (!this.selectedBridgeId) {
        this.$message.warning('请先选择桥梁')
        return
      }
      
      this.exportLoading = true
      try {
        const params = {
          statisticsType: this.statisticsTypeMapping,
          bridgeId: this.selectedBridgeId,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          ...this.detailsQuery
        }
        
        // 过滤空值
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })
        
        const blob = await exportStatisticsDetailsToCSV(params)
        
        // 创建下载链接
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.pageTitle}_详情数据_${this.formatDateTime(new Date()).replace(/[:\s]/g, '')}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      } finally {
        this.exportLoading = false
      }
    },

    // 导出数据（原有方法保留）
    exportData() {
      this.exportDetailsData()
    }
    
    // 调试方法
    /*
    debugLogData() {
      console.log('Bridge Options:', this.bridgeOptions)
      console.log('Selected Bridge ID:', this.selectedBridgeId)
      console.log('Statistics Data:', this.statisticsData)
      console.log('Chart Type:', this.chartType)
    }
    */
  }
}
</script>

<style scoped>
/* 统计分析页面样式 */
.analytics-page {
  padding: 20px;
  background: #f5f5f5;
  min-height: calc(100vh - 140px); /* 减去header和footer的高度 */
}

/* 卡片悬停效果 */
.analytics-page .bg-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

/* 图表容器 */
.analytics-page [ref*="Chart"] {
  border-radius: 8px;
}

/* 按钮样式优化 */
.analytics-page .el-button--primary {
  transition: all 0.3s ease;
}

.analytics-page .el-button--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* 表格样式 */
.analytics-page .el-table th {
  background: #fafafa !important;
}

/* 表格占位符样式 */
.table-placeholder {
  position: relative;
  min-height: 400px;
}

.empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .analytics-page {
    padding: 15px;
  }
}

@media (max-width: 768px) {
  .analytics-page {
    padding: 10px;
  }
}
</style>
